import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslationService } from '@core/services/local/translation.service';
import {
  buttonConfig,
  customComponentListener,
  fileSizeType,
  fileUploaderConfig,
  labelType,
} from '@core/types/types';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgComponentOutlet } from '@angular/common';
import {
  bootstrapFileEarmarkText,
  bootstrapXCircle,
  bootstrapXLg,
} from '@ng-icons/bootstrap-icons';

import { TranslateModule } from '@ngx-translate/core';
import { IconsComponent } from '../icons/icon.component';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { CustomButtonComponent } from '../button/button.component';
import {
  acceptedExtensionsType,
  acceptedFileTypes,
} from '@shared/constants/accepted-file-types';
import { ComponentUtilsService, ToastService } from '@core/services';

@Component({
  selector: 'file-uploader-component',
  templateUrl: './file-uploader.component.html',
  imports: [
    NgbNavModule,
    TranslateModule,
    IconsComponent,
    CustomButtonComponent,
    DndModule,
    NgComponentOutlet,
  ],
  styleUrls: ['./file-uploader.component.scss'],
})
export class CustomFileUploaderComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input({ required: true }) config!: fileUploaderConfig;
  @Input({ required: false }) data!: any;
  @Input({ required: false }) listeners!: customComponentListener[];

  @Output() onFilesAdded = new EventEmitter<{ files: File[]; ref: string }>();
  @Output() onFilesRemoved = new EventEmitter<{ file: File; ref: string }>();

  constructor(
    private translate: TranslationService,
    private utils: ComponentUtilsService,
    private toastService: ToastService
  ) {}

  protected acceptedFilesExtensions = acceptedFileTypes.all;

  protected defaultConfig = {
    icon: bootstrapFileEarmarkText,
    title: 'common.file-upload.title',
    description: 'common.file-upload.description',
    multiple: false,
    required: false,
    acceptedFiles: ['all'] as acceptedExtensionsType[],
    maxSize: {
      unit: 'kb',
      value: 100,
    } as fileSizeType,
  };

  protected selectedFiles: File[] = [];
  protected error?: labelType;
  protected isDraggingOver = signal<boolean>(false);

  protected configs = {
    btn: {
      addFile: {
        type: 'button',
        theme: 'light',
        color: 'primary',
        label: {
          key: 'common.select-file.title',
        },
        action: () => {
          this.openFileUpload();
        },
      } as buttonConfig,
      removeFile: {
        type: 'icon',
        icon: bootstrapXLg,
        theme: 'ghost',
        color: 'info',
        action: (data) => {
          const fileToRemove = this.selectedFiles.find(
            (file) => file.name == data.name
          )!;

          this.selectedFiles = this.selectedFiles.filter(
            (file) => file.name != data.name
          );

          this.onFilesRemoved.emit({
            file: fileToRemove,
            ref: this.config.ref,
          });
        },
      } as buttonConfig,
    },
  };

  ngOnInit() {
    this.utils.attachListeners(this, this.listeners);

    if (this.config.type == 'input' && this.config.customInput) {
      this.config.customInput.data.config.action = () => {
        this.openFileUpload();
      };
    }

    this.config = {
      ...this.defaultConfig,
      ...this.config,
    };

    this.acceptedFilesExtensions = this.config
      .acceptedFiles!.map((ext) => {
        return acceptedFileTypes[ext];
      })
      .join(', ');
  }

  openFileUpload() {
    this.fileInput.nativeElement.click();
  }

  onDrop(event: DndDropEvent) {
    this.selectedFiles = [];
    const nativeEvent = event.event as DragEvent;
    let files = Array.from(nativeEvent.dataTransfer?.files!);

    const { validFiles, error } = this.validateFiles(files);

    this.error = error;

    if (!error) {
      this.selectedFiles = files;
      this.onFilesAdded.emit({
        files: this.selectedFiles,
        ref: this.config.ref,
      });
    } else {
      if (this.config.type == 'input' && this.config.customInput) {
        this.toastService.show({
          ref: 'fileUploadError',
          color: 'tertiary',
          delay: 3000,
          message: {
            key: error.key,
            params: error.params
          },
          icon: bootstrapXCircle,
        });
      }
    }
    this.isDraggingOver.set(false);
  }

  onFileInputChange(event: Event) {
    this.selectedFiles = [];

    event.preventDefault();

    const files: File[] = Array.from(
      (event.target as HTMLInputElement).files || []
    );

    const { validFiles, error } = this.validateFiles(files);

    this.error = error;

    if (!error) {
      this.selectedFiles = files;
      this.onFilesAdded.emit({
        files: this.selectedFiles,
        ref: this.config.ref,
      });
      return;
    } else {
      if (this.config.type == 'input' && this.config.customInput) {
        this.toastService.show({
          ref: 'fileUploadError',
          color: 'tertiary',
          delay: 3000,
          message: {
            key: error.key,
            params: error.params
          },
          icon: bootstrapXCircle,
        });
      }
    }

    this.fileInput.nativeElement.value = '';
  }

  validateFiles(files: File[]) {
    const validFiles: File[] = [];
    let error: labelType | undefined;

    if (!this.config.multiple && files.length > 1)
      error = { key: 'error.file-upload.no-multiple-files' };

    files.forEach((file) => {
      if (error) return;

      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (!(this.acceptedFilesExtensions == acceptedFileTypes.all))
        if (!this.acceptedFilesExtensions.includes(extension))
          error = {
            key: 'error.file-upload.invalid-files-extensions',
            params: { validExtensions: this.acceptedFilesExtensions },
          };

      if (!this.validateFileSize(file))
        error = {
          key: 'error.file-upload.invalid-files-size',
          params: {
            maxSize: `${
              this.config.maxSize!.value
            }${this.config.maxSize!.unit.toUpperCase()}`,
          },
        };
    });

    return { validFiles: validFiles, error: error };
  }

  validateFileSize(file: File) {
    const { unit, value } = this.config.maxSize!;
    const fileSizeInBytes = file.size;

    switch (unit) {
      case 'kb':
        return fileSizeInBytes <= value * 1024;
      case 'mb':
        return fileSizeInBytes <= value * 1024 * 1024;
      case 'gb':
        return fileSizeInBytes <= value * 1024 * 1024 * 1024;
      default:
        return false;
    }
  }

  getSimpleFileInputLabel() {
    if (this.selectedFiles.length > 0)
      // if (this.selectedFiles.length <= 1)
      //   return this.selectedFiles.map((file) => file.name).join(', ');
      // else
      return this.translate.translate({
        keys: ['common.file-upload.default-selected-files'],
        separator: ' ',
        params: {
          'common.file-upload.default-selected-files': {
            amoutSelectedFiles: this.selectedFiles.length,
          },
        },
      });

    return this.translate.translate({
      keys: ['common.file-upload.no-files-chosen'],
      separator: ' ',
    });
  }
}
